import React, { useState, useRef, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from 'react-bootstrap/Button';
import {LogIn, Moon, Sun} from "lucide-react";
import AuthService from "../../services/AuthService";
import { AuthContext } from "../../context/AuthContext";
import "../../assets/css/TopTabbar.css";
import RoleBasedRender from '../RoleBasedRender';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Modal from "react-modal";


// Styles pour la modal
const customStyles = {
    content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
        padding: "20px",
        borderRadius: "8px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
        maxWidth: "500px",
        width: "100%",
        textAlign: "center",
        zIndex: 100,
    },
    overlay: {
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 10000,
    },
};

// Bind modal à l'élément root de l'application
Modal.setAppElement("#root");

// Composant LoadingSpinner intégré
const LoadingSpinner = () => (
    <div className="loading-spinner-container" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
        position: 'fixed',
        top: 0,
        left: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        zIndex: 9999
    }}>
        <div className="spinner" style={{
            width: '50px',
            height: '50px',
            border: '5px solid #f3f3f3',
            borderTop: '5px solid #24afcc',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `}</style>
    </div>
);

const TopTabbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [userName, setUserName] = useState("");
    const [userRole, setUserRole] = useState("");
    const [language, setLanguage] = useState("FR");
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const sideMenuRef = useRef(null);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const minSwipeDistance = 50;
    const [profileImageUrl, setProfileImageUrl] = useState(null);

    const { logout } = useContext(AuthContext);
    const navigate = useNavigate();

    const handleLogout = async () => {
        setIsLoading(true);
        try {
            await new Promise((resolve) => setTimeout(resolve, 3000));
            await logout();
            navigate("/login");
        } catch (error) {
            console.error("Erreur lors de la déconnexion :", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const user = AuthService.getCurrentUser();
        if (user) {
            setUserName(`${user.firstName} ${user.lastName}`);
            setUserRole(user.roleNames[0]);
            setProfileImageUrl(user.profileImageUrl);
        }

        const prefersDarkMode = window.matchMedia("(prefers-color-scheme: dark)").matches;
        const savedMode = localStorage.getItem("darkMode");
        const initialMode = savedMode !== null ? JSON.parse(savedMode) : prefersDarkMode;
        setIsDarkMode(initialMode);
        applyDarkMode(initialMode);
    }, []);

    const toggleDarkMode = () => {
        const newMode = !isDarkMode;
        setIsDarkMode(newMode);
        applyDarkMode(newMode);
        localStorage.setItem("darkMode", JSON.stringify(newMode));
    };

    const applyDarkMode = (isDark) => {
        if (isDark) {
            document.documentElement.classList.add("dark");
            document.body.classList.add("dark-mode");
        } else {
            document.documentElement.classList.remove("dark");
            document.body.classList.remove("dark-mode");
        }
    };

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.touches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.touches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        if (isLeftSwipe && isMenuOpen) {
            setIsMenuOpen(false);
        } else if (isRightSwipe && !isMenuOpen) {
            setIsMenuOpen(true);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sideMenuRef.current && !sideMenuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleCopyLink = () => {
        const inviteLink = "https://ntrfinancial.stelotechconsulting.com/";
        navigator.clipboard.writeText(inviteLink)
            .then(() => toast.success("Lien copié dans le presse-papiers !"))
            .catch(() => toast.error("Erreur lors de la copie du lien."));
    };

    // Afficher le spinner si isLoading est true
    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <>
            <div className="top-tabbar">
                <div className="left-section">
                    <div className="menu-button" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                        <div className="menu-icon">
                            <div className="menu-bar"></div>
                            <div className="menu-bar"></div>
                            <div className="menu-bar"></div>
                        </div>
                    </div>
                    <div className="logo-container">
                        <Link to="/" className="app-name">NTR Finance</Link>
                    </div>
                </div>

                <div className="right-section my-2">
                    <div className="user-info flex items-center gap-3">
                        <select
                            style={{ border: "none" }}
                            value={language}
                            disabled
                            onChange={(e) => setLanguage(e.target.value)}
                            className="text-dark-emphasis bg-transparent border-none outline-none cursor-pointer"
                        >
                            <option className="text-dark-emphasis" value="FR">
                                FR
                            </option>
                            <option className="text-dark-emphasis" value="EN">
                                EN
                            </option>
                        </select>
                        <button
                            style={{ border: "none" }}
                            onClick={toggleDarkMode}
                            className="text-dark-emphasis bg-transparent hover:text-yellow-500 dark:hover:text-blue-500"
                            aria-label={isDarkMode ? "Activer le mode clair" : "Activer le mode sombre"}
                        >
                            {isDarkMode ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
                        </button>
                        <Button variant="danger" onClick={handleLogout} className="logout-button">

                            <LogIn className="login-icon me-2" />
                            <span className="logout-text">Déconnexion</span>
                        </Button>
                    </div>
                </div>

                <div className="welcome-message">
                    Bonjour {userName}, bon retour dans l'application ! 😉
                </div>
            </div>

            <div
                ref={sideMenuRef}
                className={`side-menu ${isMenuOpen ? "open" : ""}`}
                style={{
                    transform: isMenuOpen ? "translateX(0)" : "translateX(-100%)",
                    opacity: isMenuOpen ? 1 : 0,
                }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
            >
                <button className="close-button" onClick={() => setIsMenuOpen(false)}>
                    <span className="close-icon">×</span>
                </button>
                <div className="menu-content">
                    <h3 className="menu-title">Menu</h3>
                    <div className="verify-number-content">
                        <div className="setting-bottom-sec">
                            <div className="setting-top">
                                <Link to="/profile">
                                    <div className="send-money-contact-tab p-0">
                                        <div className="contact-profile">
                                            {profileImageUrl ? (
                                                <img
                                                    style={{ height: "80px", width: "80px" }}
                                                    src={profileImageUrl}
                                                    alt="profile-img"
                                                />
                                            ) : (
                                                <img
                                                    style={{ height: "80px", width: "80px" }}
                                                    src="assets/images/invite-friend/avatar.jpg"
                                                    alt="profile-img"
                                                />
                                            )}
                                        </div>
                                        <div className="contact-details">
                                            <h3>{userName}</h3>
                                            {/*<h4>{userRole}</h4>*/}
                                        </div>
                                    </div>
                                </Link>
                            </div>

                            <div className="setting-bottom">
                                <Link to="/">
                                    <div className="send-money-contact-tab setting-border">
                                        <div className="setting-icon">
                                            <i className="fa-solid fa-house"
                                               style={{fontSize: "24px", color: "#7b51f1"}}></i>
                                        </div>
                                        <div className="setting-title">
                                            <h3>Accueil</h3>
                                        </div>
                                    </div>
                                </Link>

                                <Link to="/recipient">
                                    <div className="send-money-contact-tab setting-border">
                                        <div className="setting-icon">
                                            <i className="fa-duotone fa-solid fa-id-card"
                                               style={{fontSize: "24px", color: "#7b51f1"}}></i>
                                        </div>
                                        <div className="setting-title">
                                            <h3>Destinataires</h3>
                                        </div>
                                    </div>
                                </Link>

                                <Link to="/transfer">
                                    <div className="send-money-contact-tab setting-border">
                                        <div className="setting-icon">
                                            <i className="fa-solid fa-dollar-sign"
                                               style={{fontSize: "24px", color: "#7b51f1"}}></i>
                                        </div>
                                        <div className="setting-title">
                                            <h3>Transfert</h3>
                                        </div>
                                    </div>
                                </Link>

                                <RoleBasedRender roles={['ADMIN']}>
                                    <Link to="/users">
                                        <div className="send-money-contact-tab setting-border">
                                            <div className="setting-icon">
                                                <img src="assets/images/setting/setting2.svg" alt="setting-icon"/>
                                            </div>
                                            <div className="setting-title">
                                                <h3>Utilisateurs</h3>
                                            </div>
                                        </div>
                                    </Link>
                                </RoleBasedRender>

                                <RoleBasedRender roles={['ADMIN']}>
                                    <Link to="/account-deletion-requests">
                                        <div className="send-money-contact-tab setting-border">
                                            <div className="setting-icon">
                                                <img src="assets/images/setting/setting2.svg" alt="setting-icon"/>
                                            </div>
                                            <div className="setting-title">
                                                <h3>Demandes de suppression</h3>
                                            </div>
                                        </div>
                                    </Link>
                                </RoleBasedRender>

                                <Link to="/identity">
                                    <div className="send-money-contact-tab setting-border">
                                        <div className="setting-icon">
                                            <i className="fa-solid fa-passport"
                                               style={{fontSize: "24px", color: "#7b51f1"}}></i>
                                        </div>
                                        <div className="setting-title">
                                            <h3>Identification</h3>
                                        </div>
                                    </div>
                                </Link>

                                <Link to="/profile">
                                    <div className="send-money-contact-tab setting-border">
                                        <div className="setting-icon">
                                            <i className="fa-solid fa-user-gear"
                                               style={{fontSize: "24px", color: "#7b51f1"}}></i>
                                        </div>
                                        <div className="setting-title">
                                            <h3>Mon Profile</h3>
                                        </div>
                                    </div>
                                </Link>

                                <RoleBasedRender roles={['ADMIN']}>
                                    <Link to="/settings">
                                        <div className="send-money-contact-tab setting-border pt-0 mt-24">
                                            <div className="setting-icon">
                                                <i className="fa-solid fa-gear"
                                                   style={{fontSize: "24px", color: "#7b51f1"}}></i>
                                            </div>
                                            <div className="setting-title">
                                                <h3>Paramétrages</h3>
                                            </div>
                                        </div>
                                    </Link>
                                </RoleBasedRender>

                                <div className="send-money-contact-tab setting-border">
                                    <div className="setting-icon">
                                        <svg className="dark" xmlns="http://www.w3.org/2000/svg" height="24px"
                                             viewBox="0 0 24 24" width="24px" fill="#7B51F1">
                                            <g></g>
                                            <g>
                                                <g>
                                                    <g>
                                                        <path
                                                            d="M11.57,2.3c2.38-0.59,4.68-0.27,6.63,0.64c0.35,0.16,0.41,0.64,0.1,0.86C15.7,5.6,14,8.6,14,12s1.7,6.4,4.3,8.2 c0.32,0.22,0.26,0.7-0.09,0.86C16.93,21.66,15.5,22,14,22c-6.05,0-10.85-5.38-9.87-11.6C4.74,6.48,7.72,3.24,11.57,2.3z"></path>
                                                    </g>
                                                </g>
                                            </g>
                                        </svg>
                                    </div>
                                    <div className="setting-title">
                                        <h3>Mode Sombre</h3>
                                    </div>
                                    <div className="contact-star">
                                        <div className="notification-option-switch">
                                            <label className="switch" htmlFor="switch1">
                                                <input
                                                    type="checkbox"
                                                    id="switch1"
                                                    checked={isDarkMode}
                                                    onChange={toggleDarkMode}
                                                />
                                                <span className="slider theme-change"></span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <Link to="/reset-password">
                                    <div className="send-money-contact-tab setting-border">
                                        <div className="setting-icon">
                                            <img src="assets/images/setting/setting10.svg" alt="setting-icon"/>
                                        </div>
                                        <div className="setting-title">
                                            <h3>Securité</h3>
                                        </div>
                                    </div>
                                </Link>

                                <Link to="/support">
                                    <div className="send-money-contact-tab setting-border">
                                        <div className="setting-icon">
                                            <i
                                                className="fa-solid fa-circle-question"
                                                style={{
                                                    fontSize: '25px',
                                                    marginLeft: '8px',
                                                    color: '#7b51f1'
                                                }}
                                            ></i>
                                        </div>
                                        <div className="setting-title">
                                            <h3>Support</h3>
                                        </div>
                                    </div>
                                </Link>

                                <div className="send-money-contact-tab setting-border" onClick={() => setIsInviteModalOpen(true)}>
                                    <div className="setting-icon">
                                        <img src="assets/images/setting/setting20.svg" alt="setting-icon"/>
                                    </div>
                                    <div className="setting-title">
                                        <h3>Inviter des Amis</h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isMenuOpen && (
                <div
                    className="overlay"
                    onClick={() => setIsMenuOpen(false)}
                    style={{opacity: isMenuOpen ? 1 : 0}}
                />
            )}

            {/* Modal d'invitation */}
            <Modal
                isOpen={isInviteModalOpen}
                onRequestClose={() => setIsInviteModalOpen(false)}
                style={customStyles}
                contentLabel="Inviter des Amis"
            >
                <div className="modal_invitfriend p-3">
                    <h3>Inviter des Amis</h3>
                    <p>Partagez ce lien pour inviter vos amis :</p>

                    <span style={{
                        marginBottom: '10px',
                        borderRadius: '8%',
                        backgroundColor: '#17c3177d',
                        padding: '10px',
                        border: '2px dashed #308d18',
                    }}>
                        <a href="https://ntrfinancial.com/" target="_blank" rel="noopener noreferrer" style={{
                            padding: '25px',
                            paddingTop: '25px',
                            display: 'inline-block',
                        }}>
                            https://ntrfinancial.com/
                        </a>
                    </span>

                    <br />

                    <span>
                        <Button className="me-4" variant="success" onClick={handleCopyLink}>Copier le lien</Button>
                        <Button variant="secondary" onClick={() => setIsInviteModalOpen(false)}>Fermer</Button>
                    </span>
                </div>
            </Modal>

            <ToastContainer/>
        </>
    );
};

export default TopTabbar;
